/**
 * Zustand store for centralized market data management
 * 
 * Provides a single source of truth for all market information including:
 * - Markets list and metadata
 * - Orderbooks
 * - Tickers/statistics
 * - Recent trades
 * 
 * Features:
 * - Automatic caching with timestamps
 * - Loading state management
 * - Error handling
 * - Efficient lookups by ID and symbol
 * 
 * Note: Markets use Market type from store/types (API Market + id, active).
 */

import { create } from 'zustand';
import type { MarketStoreState, Market, OrderbookData, TickerData, RecentTrade } from './types';

export const useMarketStore = create<MarketStoreState>((set, get) => ({
  // Initial state
  markets: [],
  marketsById: new Map(),
  marketsBySymbol: new Map(),
  orderbooks: new Map(),
  tickers: new Map(),
  recentTrades: new Map(),
  lastUpdated: new Map(),
  selectedMarketId: null,
  
  loadingMarkets: false,
  loadingOrderbooks: new Set(),
  loadingTickers: new Set(),
  loadingTrades: new Set(),
  errors: new Map(),
  
  // Market management
  setMarkets: (markets: Market[]) => {
    const marketsById = new Map<string, Market>();
    const marketsBySymbol = new Map<string, Market>();
    
    markets.forEach(market => {
      marketsById.set(market.index, market);
      if (market.moniker) {
        marketsBySymbol.set(market.moniker, market);
      }
      if (market.symbol) {
        marketsBySymbol.set(market.symbol, market);
      }
      if (market.name) {
        marketsBySymbol.set(market.name, market);
      }
    });
    
    const state = get();
    // Auto-select first active market (or first market) if none selected
    let selectedMarketId = state.selectedMarketId;
    if (!selectedMarketId && markets.length > 0) {
      const firstActiveMarket = markets.find((m) => m.active);
      const selectedMarket = firstActiveMarket || markets[0];
      const marketIdValue = selectedMarket.index;
      if (marketIdValue) {
        selectedMarketId = marketIdValue;
      }
    }
    
    set({
      markets,
      marketsById,
      marketsBySymbol,
      selectedMarketId,
      lastUpdated: new Map(get().lastUpdated).set('markets', Date.now()),
    });
  },
  
  setMarket: (market: Market) => {
    const state = get();
    const marketsById = new Map(state.marketsById);
    const marketsBySymbol = new Map(state.marketsBySymbol);
    
    marketsById.set(market.index, market);
    if (market.moniker) {
      marketsBySymbol.set(market.moniker, market);
    }
    if (market.symbol) {
      marketsBySymbol.set(market.symbol, market);
    }
    if (market.name) {
      marketsBySymbol.set(market.name, market);
    }
    
    // Update markets array if not already present
    const markets = state.markets.find(m => m.index === market.index)
      ? state.markets.map(m => m.index === market.index ? market : m)
      : [...state.markets, market];
    
    set({
      markets,
      marketsById,
      marketsBySymbol,
    });
  },
  
  getMarket: (marketId: string) => {
    return get().marketsById.get(marketId);
  },
  
  getMarketBySymbol: (symbol: string) => {
    return get().marketsBySymbol.get(symbol);
  },
  
  setSelectedMarketId: (marketId: string | null) => {
    set({ selectedMarketId: marketId });
  },
  
  // Orderbook management
  setOrderbook: (marketId: string, orderbook: OrderbookData) => {
    const state = get();
    const orderbooks = new Map(state.orderbooks);
    orderbooks.set(marketId, {
      ...orderbook,
      timestamp: Date.now(),
    });
    
    set({
      orderbooks,
      lastUpdated: new Map(state.lastUpdated).set(marketId, Date.now()),
    });
  },
  
  getOrderbook: (marketId: string) => {
    return get().orderbooks.get(marketId);
  },
  
  updateOrderbook: (marketId: string, delta: Partial<OrderbookData>) => {
    const state = get();
    const current = state.orderbooks.get(marketId);
    
    if (current) {
      const updated: OrderbookData = {
        ...current,
        ...delta,
        timestamp: Date.now(),
      };
      state.setOrderbook(marketId, updated);
    } else {
      // If no existing orderbook, create new one from delta
      state.setOrderbook(marketId, {
        bids: delta.bids || [],
        asks: delta.asks || [],
        timestamp: Date.now(),
      });
    }
  },
  
  // Ticker management
  setTicker: (marketId: string, ticker: TickerData) => {
    const state = get();
    const tickers = new Map(state.tickers);
    tickers.set(marketId, {
      ...ticker,
      marketId,
      timestamp: Date.now(),
    });
    
    set({
      tickers,
      lastUpdated: new Map(state.lastUpdated).set(`ticker_${marketId}`, Date.now()),
    });
  },
  
  getTicker: (marketId: string) => {
    return get().tickers.get(marketId);
  },
  
  // Recent trades management
  setRecentTrades: (marketId: string, trades: RecentTrade[]) => {
    const state = get();
    const recentTrades = new Map(state.recentTrades);
    recentTrades.set(marketId, trades);
    
    set({
      recentTrades,
      lastUpdated: new Map(state.lastUpdated).set(`trades_${marketId}`, Date.now()),
    });
  },
  
  getRecentTrades: (marketId: string) => {
    return get().recentTrades.get(marketId);
  },
  
  // Loading state management
  setLoading: (key: string, loading: boolean) => {
    const state = get();
    
    if (key === 'markets') {
      set({ loadingMarkets: loading });
    } else if (key.startsWith('orderbook_')) {
      const marketId = key.split('_').slice(1).join('_'); // Get everything after 'orderbook_'
      const loadingOrderbooks = new Set(state.loadingOrderbooks);
      if (loading) {
        loadingOrderbooks.add(marketId);
      } else {
        loadingOrderbooks.delete(marketId);
      }
      set({ loadingOrderbooks });
    } else if (key.startsWith('ticker_')) {
      const marketId = key.split('_').slice(1).join('_'); // Get everything after 'ticker_'
      const loadingTickers = new Set(state.loadingTickers);
      if (loading) {
        loadingTickers.add(marketId);
      } else {
        loadingTickers.delete(marketId);
      }
      set({ loadingTickers });
    } else if (key.startsWith('trades_')) {
      const marketId = key.split('_').slice(1).join('_'); // Get everything after 'trades_'
      const loadingTrades = new Set(state.loadingTrades);
      if (loading) {
        loadingTrades.add(marketId);
      } else {
        loadingTrades.delete(marketId);
      }
      set({ loadingTrades });
    }
  },
  
  // Error management
  setError: (key: string, error: string | null) => {
    const state = get();
    const errors = new Map(state.errors);
    if (error) {
      errors.set(key, error);
    } else {
      errors.delete(key);
    }
    set({ errors });
  },
  
  clearError: (key: string) => {
    get().setError(key, null);
  },
  
  // Cache management
  clearCache: (marketId?: string) => {
    const state = get();
    
    if (marketId) {
      // Clear specific market cache
      const orderbooks = new Map(state.orderbooks);
      const tickers = new Map(state.tickers);
      const recentTrades = new Map(state.recentTrades);
      const lastUpdated = new Map(state.lastUpdated);
      
      orderbooks.delete(marketId);
      tickers.delete(marketId);
      recentTrades.delete(marketId);
      lastUpdated.delete(marketId);
      lastUpdated.delete(`ticker_${marketId}`);
      lastUpdated.delete(`trades_${marketId}`);
      
      set({ orderbooks, tickers, recentTrades, lastUpdated });
    } else {
      // Clear all cache except markets list
      set({
        orderbooks: new Map(),
        tickers: new Map(),
        recentTrades: new Map(),
        lastUpdated: new Map(),
      });
    }
  },
  
  clearAllCache: () => {
    set({
      markets: [],
      marketsById: new Map(),
      marketsBySymbol: new Map(),
      orderbooks: new Map(),
      tickers: new Map(),
      recentTrades: new Map(),
      lastUpdated: new Map(),
      errors: new Map(),
      selectedMarketId: null,
    });
  },
}));
